// The church sign-up page's script (ChurchAccountForm::Component). It shows
// each section once the answers before it are given, looks churches up as the
// visitor types, fills in a picked church, keeps the mailing address following
// the address while "same as address" is Yes, and guards "Create account".
//
// The conditions come from ChurchAccountForm::Reveal, as JSON on the page, and
// `satisfied` reads them as Reveal.satisfied? does on the server.

const SEARCH_DELAY_MS = 400
const SUBMIT_RETRY_AFTER_MS = 5000

const BORDERS = {
  present: "border-green-500",
  blank: "border-gray-300",
  error: "border-red-500"
}
const LOCKED = ["bg-gray-100", "cursor-not-allowed"]
const SUBMIT_ENABLED = ["bg-indigo-600", "hover:bg-indigo-700", "focus:outline-none", "focus:ring-2", "focus:ring-offset-2", "focus:ring-indigo-500"]
const SUBMIT_DISABLED = ["bg-gray-300"]

// The church's address is address_<part>, the mailing address mailing_address_<part>.
const ADDRESS_PARTS = ["street_1", "street_2", "city", "state", "postal_code"]

// What picking a church fills in, and answering No clears.
const CHURCH_FIELDS = ["church_id", "church_name", ...ADDRESS_PARTS.map(part => `address_${part}`)]

// Street address line 2 is optional, so it looks answered once the city is
// (ChurchAccountForm::Street2::Component#completed?).
const ANSWERED_WITH = { address_street_2: "address_city" }

// The search box is not a field, but a church that can no longer be claimed is
// refused there.
const SEARCH_ERROR = "church_search_query"

function present(value) {
  return (value ?? "").trim() !== ""
}

function satisfied(condition, values) {
  const [[operator, operands]] = Object.entries(condition)
  switch (operator) {
    case "present": return operands.every(field => present(values[field]))
    case "blank": return operands.every(field => !present(values[field]))
    case "equals": return (values[operands[0]] ?? "") === operands[1]
    case "all": return operands.every(each => satisfied(each, values))
    case "any": return operands.some(each => satisfied(each, values))
    default: throw new Error(`Unknown condition: ${operator}`)
  }
}

export class SignUpForm {
  constructor(element) {
    this.element = element
    this.completeWhen = JSON.parse(element.dataset.signUpCompleteWhen)
    this.searchUrl = element.dataset.signUpSearchUrl
    this.reveals = Array.from(element.querySelectorAll("[data-sign-up-shown-when]"), container => [container, JSON.parse(container.dataset.signUpShownWhen)])
    this.searchBox = element.querySelector("[data-sign-up-search]")
    this.results = element.querySelector("#church_search_results_component_container")
    this.submitButton = element.querySelector("#submit")
    this.searchTimer = null
    this.latestSearch = 0
    this.submitting = false
    this.submitRetryTimer = null
  }

  connect() {
    this.element.addEventListener("input", this.answered)
    this.element.addEventListener("change", this.answered)
    this.element.addEventListener("click", this.picked)
    this.element.addEventListener("submit", this.submitted)
    window.addEventListener("pageshow", this.offerSubmitAgain)
    this.refresh()
  }

  // index.js calls this on turbo:before-cache, when Turbo keeps a copy of the
  // page. Usually that is because the form is being taken off the page, but
  // not always: in Turbo 8.0.23 a native "#" navigation, such as the church
  // search's footnote link, caches the page too (historyPoppedWithEmptyState)
  // while the form stays on it. The form's own listeners go with the form, or
  // stay with it and keep it working, but the window's listener and the timers
  // would keep a form that has left alive, so they are let go.
  disconnect() {
    window.removeEventListener("pageshow", this.offerSubmitAgain)
    clearTimeout(this.searchTimer)
    clearTimeout(this.submitRetryTimer)
  }

  answered = event => {
    const control = event.target
    if (control === this.searchBox) {
      if (event.type === "input") this.searchTyped()
    } else if (control.dataset.signUpField) {
      this.clearError(control.dataset.signUpField)
      if (event.type === "change") this.followAnswer(control)
    }
    this.refresh()
  }

  // What an answer changes beyond itself.
  followAnswer(control) {
    const name = control.dataset.signUpField
    if (name === "affiliation" && control.value === "non_affiliated") {
      // No means the church is not one from the search.
      for (const field of CHURCH_FIELDS) this.setValue(field, "")
    }
    if (name === "mailing_address_is_same_as_address" && control.value === "false") {
      for (const part of ADDRESS_PARTS) this.setValue(`mailing_address_${part}`, "")
    }
  }

  picked = event => {
    const link = event.target.closest("[data-church-pick]")
    if (!link) return

    event.preventDefault()
    for (const field of CHURCH_FIELDS) this.setValue(field, link.getAttribute(`data-${field.replaceAll("_", "-")}`) ?? "")
    this.refresh()
  }

  // Looks churches up once typing pauses, and shows only the latest lookup's
  // answer. A failed lookup leaves the results as they were, and a blank box
  // clears them without asking.
  searchTyped() {
    this.clearError(SEARCH_ERROR)
    clearTimeout(this.searchTimer)
    const query = this.searchBox.value
    if (present(query)) {
      this.searchTimer = setTimeout(() => this.search(query), SEARCH_DELAY_MS)
    } else {
      this.latestSearch++
      this.results.replaceChildren()
    }
  }

  async search(query) {
    const search = ++this.latestSearch
    const url = new URL(this.searchUrl, window.location.href)
    url.searchParams.set("query", query)
    let html
    try {
      const response = await fetch(url)
      if (!response.ok) return

      html = await response.text()
    } catch {
      // Offline, or the request failed: the results stay as they were.
      return
    }
    if (search === this.latestSearch) this.results.innerHTML = html
  }

  // An incomplete sign-up isn't sent. A complete one turns the button off
  // while it is on its way, and the button comes back if the page is still
  // here a few seconds later (the visitor pressed Stop) or is shown again.
  submitted = event => {
    if (!satisfied(this.completeWhen, this.values())) {
      event.preventDefault()
      return
    }

    this.submitting = true
    this.refresh()
    this.submitRetryTimer = setTimeout(this.offerSubmitAgain, SUBMIT_RETRY_AFTER_MS)
  }

  offerSubmitAgain = () => {
    // Turbo fires turbo:before-cache only for a page it keeps a copy of, so a
    // form taken off a page it didn't keep was never disconnected.
    if (!this.element.isConnected) {
      this.disconnect()
      return
    }

    this.submitting = false
    this.refresh()
  }

  refresh() {
    const values = this.followAddress(this.values())
    for (const [container, condition] of this.reveals) container.hidden = !satisfied(condition, values)
    this.refreshLocks(values)
    this.refreshBorders(values)
    this.refreshSubmit(satisfied(this.completeWhen, values))
  }

  // While "same as address" is Yes, the mailing address is the address.
  followAddress(values) {
    if (values.mailing_address_is_same_as_address !== "true") return values

    for (const part of ADDRESS_PARTS) this.setValue(`mailing_address_${part}`, values[`address_${part}`])
    return this.values()
  }

  // A picked church's name, and the mailing address while it is the address,
  // are locked. A browser doesn't post a locked field, so the server takes them
  // from the church and from the address.
  refreshLocks(values) {
    this.lock(this.field("church_name"), present(values.church_id))
    for (const part of ADDRESS_PARTS) {
      this.lock(this.field(`mailing_address_${part}`), values.mailing_address_is_same_as_address === "true")
    }
  }

  refreshBorders(values) {
    for (const control of this.element.querySelectorAll("[data-sign-up-field]")) {
      if (control.type === "radio" || control.type === "hidden") continue

      const name = control.dataset.signUpField
      const alsoAnsweredBy = ANSWERED_WITH[name]
      const answered = present(values[name]) || (alsoAnsweredBy !== undefined && present(values[alsoAnsweredBy]))
      this.setBorder(control, this.hasError(name) ? "error" : answered ? "present" : "blank")
    }
    this.setBorder(this.searchBox, this.hasError(SEARCH_ERROR) ? "error" : "blank")
  }

  refreshSubmit(complete) {
    const enabled = complete && !this.submitting
    this.submitButton.disabled = !enabled
    this.submitButton.classList.remove(...SUBMIT_ENABLED, ...SUBMIT_DISABLED)
    this.submitButton.classList.add(...(enabled ? SUBMIT_ENABLED : SUBMIT_DISABLED))
  }

  // Every answer, by field name, as a string: a radio group's is the checked
  // one's value, or "" before one is chosen.
  values() {
    const values = {}
    for (const control of this.element.querySelectorAll("[data-sign-up-field]")) {
      const name = control.dataset.signUpField
      if (control.type === "radio") {
        values[name] ??= ""
        if (control.checked) values[name] = control.value
      } else {
        values[name] = control.value
      }
    }
    return values
  }

  field(name) {
    return this.element.querySelector(`[data-sign-up-field="${name}"]`)
  }

  setValue(name, value) {
    const control = this.field(name)
    if (control.value === value) return

    control.value = value
    this.clearError(name)
  }

  lock(control, locked) {
    control.disabled = locked
    for (const name of LOCKED) control.classList.toggle(name, locked)
  }

  setBorder(control, state) {
    control.classList.remove(...Object.values(BORDERS))
    control.classList.add(BORDERS[state])
  }

  hasError(name) {
    return this.element.querySelector(`[data-sign-up-error-for="${name}"]`) !== null
  }

  // A server error is about the answer that was sent, so it goes once the
  // answer changes.
  clearError(name) {
    for (const error of this.element.querySelectorAll(`[data-sign-up-error-for="${name}"]`)) error.remove()
  }
}
